import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { agentsApi, WorkflowRow, WorkflowRunResult } from '@/lib/agents';
import { AppShell } from '@/components/app/AppShell';
import { HelpHint } from '@/components/app/HelpHint';
import { InlineAlert, Modal } from '@/components/app/Modal';
import { useDialogs } from '@/components/app/DialogProvider';
import { CitedMarkdown } from '@/components/ui/CitedMarkdown';
import { modelTierLabel } from '@/lib/model-label';
import { useFormatDateTime } from '@/lib/datetime';
import { CronPicker } from '@/components/ui/CronPicker';
import cronstrue from 'cronstrue';

function humanizeCron(expr: string): string {
  try {
    return cronstrue.toString(expr, { use24HourTimeFormat: true });
  } catch {
    return `cron: ${expr}`;
  }
}

const DEFAULT_YAML = `name: Weekly digest
language: en
steps:
  - kind: search
    question: what changed this week
  - kind: brief
    topic: status update
    audience: steering committee
    length: medium
`;

/**
 * JP-vertical starter templates — automation INDX (an engine with no agents)
 * structurally cannot offer. One click loads a ready-to-run pipeline.
 */
interface WorkflowTemplate { id: string; label: string; name: string; yaml: string }
const WORKFLOW_TEMPLATES: WorkflowTemplate[] = [
  {
    id: 'ringi',
    label: '稟議承認ルーティング',
    name: '稟議承認ルーティング',
    yaml: `name: 稟議承認ルーティング
language: ja
steps:
  - kind: search
    question: 稟議の承認基準と決裁権限規程
  - kind: audit
    scope: jsox
    question: この稟議は決裁権限規程と職務分離に適合しているか、証拠を示せ
  - kind: human_gate
`,
  },
  {
    id: 'hanko-audit',
    label: '契約・ハンコ監査',
    name: '契約・ハンコ監査',
    yaml: `name: 契約・ハンコ監査
language: ja
steps:
  - kind: search
    question: 契約書のハンコ・押印と登録番号
  - kind: audit
    scope: general
    question: 契約書の押印・法人番号・契約条件にリスクや不備がないか
  - kind: brief
    topic: 契約レビューの要約
    audience: 法務・経営陣
    length: medium
`,
  },
  {
    id: 'minutes',
    label: '議事録ダイジェスト',
    name: '議事録ダイジェスト',
    yaml: `name: 議事録ダイジェスト
language: ja
steps:
  - kind: search
    question: 直近の取締役会・会議の議事録と決定事項
  - kind: brief
    topic: 会議の決定事項とアクションアイテム
    audience: 経営陣
    length: short
`,
  },
  {
    id: 'appi',
    label: '個人情報コンプライアンス点検',
    name: '個人情報コンプライアンス点検',
    yaml: `name: 個人情報コンプライアンス点検
language: ja
steps:
  - kind: audit
    scope: appi
    question: 個人情報の取得・利用・越境移転は法令と社内規程に適合しているか
  - kind: brief
    topic: 個人情報コンプライアンスの現状と是正点
    audience: DPO・法務
    length: medium
`,
  },
];

export function WorkflowsPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const { confirm, alert } = useDialogs();
  const onMutationError = () =>
    alert({ title: t('common.action_failed', { defaultValue: 'Something went wrong. Please try again.' }) as string });
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [yaml, setYaml] = useState(DEFAULT_YAML);
  const [schedule, setSchedule] = useState('');
  const [runResult, setRunResult] = useState<WorkflowRunResult | null>(null);

  const { data: workflows } = useQuery({
    queryKey: ['workflows', wsId],
    queryFn: () => agentsApi.listWorkflows(wsId!),
    enabled: !!wsId,
  });

  const save = useMutation({
    mutationFn: () =>
      editingId
        ? agentsApi.updateWorkflow(editingId, { name: name.trim(), yaml, schedule: schedule.trim() || null })
        : agentsApi.createWorkflow(wsId!, name, yaml, schedule.trim() || null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflows', wsId] });
      setShowNew(false);
      setEditingId(null);
      setName('');
      setSchedule('');
      setYaml(DEFAULT_YAML);
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => agentsApi.deleteWorkflow(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workflows', wsId] }),
    onError: onMutationError,
  });

  const startNew = () => { setEditingId(null); setName(''); setYaml(DEFAULT_YAML); setSchedule(''); setShowNew(true); };
  const startEdit = (wf: WorkflowRow) => {
    setEditingId(wf.id); setName(wf.name); setYaml(wf.yaml); setSchedule(wf.schedule ?? ''); setShowNew(true);
  };
  const confirmDelete = async (wf: WorkflowRow) => {
    const ok = await confirm({
      title: t('workflow.delete_confirm', { defaultValue: 'Delete workflow “{{name}}”?', name: wf.name }) as string,
      confirmLabel: t('common.delete', { defaultValue: 'Delete' }) as string,
    });
    if (ok) del.mutate(wf.id);
  };

  const run = useMutation({
    mutationFn: (id: string) => agentsApi.runWorkflow(id),
    onSuccess: (result) => setRunResult(result),
    onError: onMutationError,
  });

  return (
    <AppShell
      title={t('workflow.title', { defaultValue: 'Workflows' })}
      description={t('workflow.subtitle', { defaultValue: 'Compose multi-step agent pipelines on demand or by cron.' }) as string}
      maxWidth="max-w-4xl"
      actions={
        <button
          onClick={startNew}
          className="btn-press px-4 py-2 text-sm"
        >
          {t('workflow.new', { defaultValue: 'New workflow' })}
        </button>
      }
    >
      <div className="mb-4"><HelpHint page="workflows" /></div>
      <div className="space-y-6">
        <section>
          {!workflows || workflows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-ink-200 bg-paper-50/40 p-10 text-center">
              <h3 className="text-[15px] font-semibold text-ink-900">{t('workflow.empty', { defaultValue: 'No workflows yet' })}</h3>
              <p className="mt-1.5 text-[13px] text-ink-600 max-w-md mx-auto leading-relaxed">
                {t('workflow.empty_sub', { defaultValue: 'Chain agent steps (search → brief → compose) to run on demand or on a schedule. Human-approval gates route to Approvals.' })}
              </p>
              <button onClick={startNew} className="btn-press px-4 py-2 text-sm mt-4">
                {t('workflow.new', { defaultValue: 'New workflow' })}
              </button>
            </div>
          ) : (
            <ul className="space-y-2">
              {workflows.map((wf) => (
                <WorkflowRowItem key={wf.id} wf={wf} run={run} onEdit={startEdit} onDelete={confirmDelete} deleting={del.isPending} />
              ))}
            </ul>
          )}
        </section>

        {runResult && (
          <section className="card-paper p-5">
            <h3 className="text-sm font-semibold text-ink-900 mb-2">
              {t('workflow.last_run', { defaultValue: 'Last run' })} · {runResult.status}
            </h3>
            {runResult.status === 'pending_approval' && (
              <div className="mb-3 rounded-lg border border-citation-200 bg-citation-50 px-3 py-2 text-[12px] text-citation-700 flex items-center justify-between gap-2">
                <span>{t('workflow.gate_pending', { defaultValue: 'This run paused at a human-approval gate.' })}</span>
                <Link to={`/app/workspace/${wsId}/approvals`} className="font-semibold hover:underline">
                  {t('workflow.go_approvals', { defaultValue: 'Review in Approvals →' })}
                </Link>
              </div>
            )}
            <ol className="space-y-2">
              {runResult.steps.map((s, i) => (
                <li key={i} className="rounded-md bg-paper-100 px-3 py-2 text-xs">
                  <div className="font-mono text-ink-500">
                    {i + 1}. {s.kind}
                    {s.model && <> · {modelTierLabel(s.model)}</>}
                    {s.costCents !== undefined && <> · ¢{s.costCents}</>}
                  </div>
                  <div className="mt-1">
                    <StepOutput output={s.output} wsId={wsId} />
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>

      <Modal
        open={showNew}
        onOpenChange={setShowNew}
        title={editingId ? t('workflow.edit', { defaultValue: 'Edit workflow' }) as string : t('workflow.new', { defaultValue: 'New workflow' }) as string}
        description={t('workflow.new_subtitle', { defaultValue: 'Define a YAML pipeline of agent steps and optional cron schedule.' })}
        size="lg"
      >
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-ink-900 mb-1">{t('common.name', { defaultValue: 'Name' })}</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-900 mb-2">{t('workflow.schedule', { defaultValue: 'Schedule' })}</label>
            <CronPicker value={schedule} onChange={setSchedule} />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-900 mb-1">{t('workflow.yaml', { defaultValue: 'YAML' })}</label>
            <div className="mb-2 flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] uppercase tracking-[0.12em] text-ink-400 font-semibold">
                {t('workflow.templates', { defaultValue: 'JP templates' })}
              </span>
              {WORKFLOW_TEMPLATES.map((tpl) => (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => { setName(tpl.name); setYaml(tpl.yaml); }}
                  className="text-[12px] text-ink-700 bg-paper-100 hover:bg-accent-50 hover:text-accent-700 border border-ink-100 rounded-full px-2.5 py-1 transition"
                >
                  {tpl.label}
                </button>
              ))}
            </div>
            <textarea
              value={yaml}
              onChange={(e) => setYaml(e.target.value)}
              rows={12}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-accent-500/30"
            />
          </div>
          {save.isError && (
            <InlineAlert tone="error">
              {String((save.error as Error)?.message || 'Failed')}
            </InlineAlert>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowNew(false); setEditingId(null); }}
              className="btn-ghost px-3.5 py-2 text-sm"
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </button>
            <button
              onClick={() => save.mutate()}
              disabled={!name.trim() || !yaml.trim() || save.isPending}
              className="btn-press px-4 py-2 text-sm disabled:opacity-40"
            >
              {save.isPending ? t('common.saving', { defaultValue: 'Saving…' }) : t('workflow.submit', { defaultValue: 'Save' })}
            </button>
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}

interface RunMutation {
  mutate: (id: string) => void;
  isPending: boolean;
}

const RUN_STATUS_TONE: Record<string, string> = {
  running: 'bg-citation-50 text-citation-700 border-citation-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending_approval: 'bg-amber-50 text-amber-700 border-amber-200',
  failed: 'bg-warn-500/10 text-warn-600 border-warn-500/30',
};

function WorkflowRowItem({ wf, run, onEdit, onDelete, deleting }: {
  wf: WorkflowRow;
  run: RunMutation;
  onEdit: (wf: WorkflowRow) => void;
  onDelete: (wf: WorkflowRow) => void;
  deleting: boolean;
}) {
  const { t } = useTranslation();
  const fmt = useFormatDateTime();
  const qc = useQueryClient();
  const { alert } = useDialogs();
  const [showRuns, setShowRuns] = useState(false);

  const { data: runs = [], isLoading: runsLoading } = useQuery({
    queryKey: ['workflow-runs', wf.id],
    queryFn: () => agentsApi.listWorkflowRuns(wf.id),
    enabled: showRuns,
  });

  const resume = useMutation({
    mutationFn: (runId: string) => agentsApi.resumeWorkflowRun(runId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-runs', wf.id] });
      qc.invalidateQueries({ queryKey: ['workflows'] });
    },
    onError: () => alert({ title: t('workflow.resume_error', { defaultValue: 'Could not resume — has the step been approved?' }) as string }),
  });

  return (
    <li className="card-paper px-4 py-3 text-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-ink-900 truncate">{wf.name}</div>
          <div className="text-xs text-ink-500">
            {wf.schedule ? humanizeCron(wf.schedule) : t('common.optional', { defaultValue: 'on demand' })}
            {wf.last_run_at && ` · last run ${fmt(wf.last_run_at)}`}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setShowRuns((v) => !v)}
            className="text-xs text-ink-500 hover:text-ink-800 px-2"
          >
            {t('workflow.runs', { defaultValue: 'Runs' })} {showRuns ? '▲' : '▾'}
          </button>
          <button
            onClick={() => run.mutate(wf.id)}
            disabled={run.isPending}
            className="btn-press px-3 py-1.5 text-xs disabled:opacity-40"
          >
            {t('workflow.run_now', { defaultValue: 'Run now' })}
          </button>
          <button onClick={() => onEdit(wf)} className="btn-ghost px-3 py-1.5 text-xs">
            {t('common.edit', { defaultValue: 'Edit' })}
          </button>
          <button
            onClick={() => onDelete(wf)}
            disabled={deleting}
            className="text-xs text-rose-600 hover:text-rose-700 disabled:opacity-40 px-2"
          >
            {t('common.delete', { defaultValue: 'Delete' })}
          </button>
        </div>
      </div>

      {showRuns && (
        <div className="mt-3 border-t border-ink-100 pt-3">
          {runsLoading ? (
            <div className="text-xs text-ink-400">{t('common.loading', { defaultValue: 'Loading…' })}</div>
          ) : runs.length === 0 ? (
            <div className="text-xs text-ink-400">{t('workflow.no_runs', { defaultValue: 'No runs yet.' })}</div>
          ) : (
            <ul className="space-y-1.5">
              {runs.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className={`rounded-md px-1.5 py-0.5 border font-semibold uppercase tracking-[0.08em] text-[10px] ${RUN_STATUS_TONE[r.status] ?? 'bg-ink-100 text-ink-600 border-ink-200'}`}>
                      {t(`workflow.status.${r.status}`, { defaultValue: r.status })}
                    </span>
                    <span className="text-ink-400 num">{fmt(r.started_at)}</span>
                  </div>
                  {r.status === 'pending_approval' && (
                    <button
                      onClick={() => resume.mutate(r.id)}
                      disabled={resume.isPending}
                      className="text-accent-600 hover:underline disabled:opacity-40"
                    >
                      {t('workflow.resume', { defaultValue: 'Resume' })}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

// Workflow step output is shape-dependent: compose/brief/audit/compare return
// prose-shaped fields (rendered with citations); search/retrieve return hit
// arrays (rendered as a compact result list). Raw JSON is the last resort.
function StepOutput({ output, wsId }: { output: unknown; wsId?: string }) {
  if (output && typeof output === 'object') {
    const o = output as Record<string, unknown>;
    const prose =
      (typeof o.draft === 'string' && o.draft) ||
      (typeof o.answer === 'string' && o.answer) ||
      (typeof o.summary === 'string' && o.summary) ||
      (typeof o.body === 'string' && o.body);
    if (prose) return <CitedMarkdown content={prose} wsId={wsId} className="text-ink-900" />;

    // search/retrieve steps: render hits as a readable list, not raw JSON.
    const hits = (Array.isArray(o.hits) && o.hits) || (Array.isArray(o.results) && o.results) ||
      (Array.isArray(output) ? (output as unknown[]) : null);
    if (Array.isArray(hits) && hits.length) {
      return (
        <ul className="space-y-1.5">
          {hits.slice(0, 12).map((h, i) => {
            const hit = (h ?? {}) as Record<string, unknown>;
            const title = (hit.title || hit.documentTitle || hit.name || `Result ${i + 1}`) as string;
            const snippet = (hit.snippet || hit.text || hit.content || '') as string;
            return (
              <li key={i} className="rounded-md bg-paper-50 border border-ink-100 px-2.5 py-1.5">
                <div className="text-[12.5px] font-medium text-ink-800 truncate">{title}</div>
                {snippet && <div className="text-[11.5px] text-ink-500 line-clamp-2">{snippet}</div>}
              </li>
            );
          })}
        </ul>
      );
    }
  }
  return (
    <pre className="whitespace-pre-wrap font-mono text-[11.5px] text-ink-700 overflow-x-auto">
      {JSON.stringify(output, null, 2)}
    </pre>
  );
}
